import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Wallet, HandCoins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EntryLine = ({ e, checked, onToggle }) => (
  <label className="flex items-center gap-2 text-sm py-1" data-testid={`cash-entry-${e.id}`}>
    {onToggle && <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`cash-entry-check-${e.id}`} />}
    <span className="flex-1 min-w-0 truncate">{e.receipt_no || "Pending"} · {e.student_name} ({e.roll_number})</span>
    <span className="text-xs text-slate-400 hidden sm:inline">{formatDateTime(e.created_at)}</span>
    <b className="shrink-0">{inr(e.cash_amount)}</b>
  </label>
);

export function ReceiveGroup({ g, endpoint, extra = {}, onDone, testid }) {
  const [sel, setSel] = useState(() => Object.fromEntries(g.entries.map((e) => [e.id, true])));
  const [actual, setActual] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ids = g.entries.filter((e) => sel[e.id]).map((e) => e.id);
  const expected = g.entries.filter((e) => sel[e.id]).reduce((s, e) => s + (e.cash_amount || 0), 0);
  const diff = actual === "" ? 0 : (parseInt(actual, 10) || 0) - expected;
  const confirm = async () => {
    if (!ids.length) { toast.error("Tick at least one entry"); return; }
    if (diff !== 0 && !reason.trim()) { toast.error("Add a reason for the difference"); return; }
    setBusy(true);
    try {
      await api.post(endpoint, { ...extra, collection_ids: ids, received_amount: actual === "" ? expected : parseInt(actual, 10) || 0, reason });
      toast.success(`Received ${inr(actual === "" ? expected : actual)} from ${g.teacher_name}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2"><p className="font-bold text-slate-900">{g.teacher_name}</p><span className="text-sm font-semibold">{g.entries.length} entr{g.entries.length === 1 ? "y" : "ies"} · {inr(g.total)}</span></div>
      <div className="divide-y divide-slate-100">{g.entries.map((e) => <EntryLine key={e.id} e={e} checked={!!sel[e.id]} onToggle={() => setSel((s) => ({ ...s, [e.id]: !s[e.id] }))} />)}</div>
      <div className="flex flex-wrap items-end gap-2">
        <div><p className="text-[11px] text-slate-500">Counted amount (leave blank if {inr(expected)})</p><Input type="number" inputMode="numeric" className="h-9 w-36" value={actual} onChange={(e) => setActual(e.target.value)} placeholder={String(expected)} data-testid={`${testid}-actual`} /></div>
        {diff !== 0 && <Input className="h-9 flex-1 min-w-[160px]" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for difference (required)" data-testid={`${testid}-reason`} />}
        <Button onClick={confirm} disabled={busy || !ids.length} className="bg-green-600 hover:bg-green-700" data-testid={`${testid}-confirm`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm received ${inr(actual === "" ? expected : actual)}`}</Button>
      </div>
      {diff !== 0 && <p className={`text-xs font-semibold ${diff < 0 ? "text-red-600" : "text-amber-600"}`} data-testid={`${testid}-diff`}>Difference: {diff > 0 ? "+" : ""}{inr(diff)} — will be flagged</p>}
    </div>
  );
}

export default function CashHandover() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/cash/me").then(({ data }) => setD(data)).catch(() => toast.error("Could not load cash")), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  return (
    <div className="max-w-3xl space-y-6" data-testid="cash-handover">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-teal-600 text-white p-5 flex items-center gap-4">
        <Wallet className="h-8 w-8 shrink-0" />
        <div><p className="text-xs uppercase tracking-widest text-emerald-100 font-semibold">Cash with me</p><p className="text-3xl font-extrabold" data-testid="cash-with-me">{inr(d.with_me)}</p>
          <p className="text-xs text-emerald-100">{d.is_head ? "Your own cash collections are counted in \"Cash I'm holding\" below." : "Hand this to the head teacher. It clears once she confirms."}</p></div>
      </div>
      {d.entries.length > 0 && <div className="rounded-xl border border-slate-200 bg-white p-3 divide-y divide-slate-100" data-testid="cash-my-entries">{d.entries.map((e) => <EntryLine key={e.id} e={e} />)}</div>}

      {d.is_head && (<>
        <div className="space-y-3" data-testid="cash-head-collect">
          <h3 className="flex items-center gap-2 font-bold text-slate-900"><HandCoins className="h-5 w-5" /> Collect from teachers</h3>
          {d.pending_from_teachers.length === 0 ? <p className="text-sm text-slate-500">No cash waiting with teachers.</p>
            : d.pending_from_teachers.map((g) => <ReceiveGroup key={`${g.teacher_id}-${g.entries.length}`} g={g} endpoint="/cash/head/receive" extra={{ teacher_id: g.teacher_id }} onDone={load} testid={`cash-head-group-${g.teacher_id}`} />)}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="cash-head-holding">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Cash I'm holding (to give the office)</p>
          <p className="text-2xl font-extrabold text-slate-900" data-testid="cash-head-holding-total">{inr(d.holding_total)}</p>
          {d.holding.map((g) => <p key={g.teacher_id} className="text-sm text-slate-600">{g.teacher_name}: {inr(g.total)} ({g.entries.length})</p>)}
        </div>
      </>)}
    </div>
  );
}
